import { ActionTree } from 'vuex';
import { RestaurantState, RootState } from '../types';
import { state } from './restaurant';
import { state as authState } from '../auth/auth';
import { getMenuStyle } from '@/utils/styling';
import { DateTime } from 'luxon';
import Router, { Route } from 'vue-router';
import http from '@/utils/http';
import i18n from '@/i18n';

export const actions: ActionTree<RestaurantState, RootState> = {
	/**
	 * We fetch the initial menus and the restaurant to gather the configuration like default
	 * tax_rate, orders paused and styling options.
	 * We then process all query parameters.
	 *
	 * @param {FetchRestaurantPayload} payload
	 * @return {Promise<void>}
	 */
	async fetchRestaurant({ commit, dispatch }, { restaurantSlug, route, router }: FetchRestaurantPayload): Promise<void> {
		try {
			// Get menus and restaurant info
			const { menuGroups, menus, restaurant } = await http.getData(restaurantSlug) as HttpPayload;
			commit('SET_RESTAURANT', restaurant);
			commit('cart/SET_RESTAURANT_CHARGES', restaurant, { root: true });
			dispatch('auth/setAxiosInstance', restaurant, { root: true });
			commit('SET_INITIAL_MENU_GROUPS', menuGroups);
			commit('SET_INITIAL_MENUS', menus);
			dispatch('filters/setAvailableFilters', '', { root: true });

			// TODO See if we can combine those with the getUserInfo to avoid multiple requests overhead
			if (authState.authenticated) {
				await dispatch('auth/getUserSnapshot', { userId: authState.user.id, token: authState.user.token }, { root: true });
				if (authState.user.credits?.balance) {
					dispatch('cart/applyCredits', true, { root: true });
				}
			}

			// Get member cookie
			await dispatch('auth/getMemberCookie', '', { root: true });

			// Get restaurant customization
			commit('SET_CSS_LOADED', await getMenuStyle(restaurant));

			try {
				await dispatch('cart/processQueryParams', route.query, { root: true });
				// If k12 location get date range and fetch rotational menus
				if (restaurant.k12) {
					dispatch('getK12DateRange', { restaurant, route, router });
				}
			}
			catch(e) {
				const error: any = e;
				throw {
					errorMessage: error.response?.data?.message || error
				};
			}
		}
		catch (e) {
			const error: any = e;
			throw {
				error,
				errorMessage: error.errorMessage || ''
			};
		}
	},

	setRestaurant({ commit }, restaurant: Restaurant): void {
		commit('SET_RESTAURANT', restaurant);
	},

	/**
	 * Set the final filtered menus by all types (member menus, query params, etc)
	 *
	 * @param {Menu[]} filteredMenus
	 * @return {Promise}
	 */
	setFilteredMenus({ commit }, filteredMenus: Menu[]): void {
		commit('SET_FILTERED_MENUS', filteredMenus);
	},

	/**
	 * Apply member pricing discounts to items/options (sets adjustedPrice)
	 *
	 * @param {Menu[]} menus
	 * @return {Promise}
	 */
	async setMemberPrices({ commit, dispatch }, menus: Menu[]): Promise<Menu[]> {
		return await new Promise((resolve) => {
			commit('SET_MEMBER_PRICES', menus);

			// Calculate cart items adjusted prices (member discount)
			dispatch('cart/calculateCartItemsAdjustedPrices', {}, { root: true });
			resolve(state.memberMenus);
		});
	},

	/**
	 * Apply subsidy pricing discounts to items/options (sets adjustedPrice)
	 *
	 * @param {Menu[]} menus
	 * @return {Promise<Menu[]>}
	 */
	async setSubsidyPrices({ commit, dispatch }, menus: Menu[]): Promise<Menu[]> {
		return await new Promise((resolve) => {
			commit('SET_SUBSIDY_PRICES', menus);

			// Calculate cart items adjusted prices (subsidy discount)
			dispatch('cart/calculateCartItemsAdjustedPrices', {}, { root: true });

			// TODO - Need additional testing to ensure that subsidy works as expected for normal menus (non rotational)
			resolve(state.memberMenus);
		});
	},

	/**
	 * Get the payment config from salata of the restaurant
	 *
	 * @return {Promise}
	 */
	async getSalataRestaurantPaymentConfig({ commit }): Promise<any> {
		const payload = {
			app8RestaurantId: state.restaurant.app8_restaurant
		};

		return await new Promise((resolve, reject) => {
			authState.axiosInstance
				.post(`${process.env.VUE_APP_API_PREFIX}/payment/get-payment-config`, payload)
				.then(response => {
					commit('SET_PAYMENT_CONFIG', response.data);
					resolve(response.data);
				})
				.catch(error => {
					reject(error);
				});
		});
	},

	/**
	 * Determine which week's menus to show based on the current date and the restaurant's order cutoff date
	 * Then fetch the rotational menus for that week
	 *
	 * @param {FetchRestaurantPayload} payload
	 * @return {Promise<void>}
	 */
	async getK12DateRange({ dispatch }, { restaurant, route, router }: { restaurant: Restaurant, route: Route, router: Router }): Promise<void> {
		const dateRange = state.rotationalMenuDateRange;
		let now = DateTime.now().setZone(restaurant.menu_configuration?.zone || 'America/Toronto');
		let beforeOrderCutoff = false;
		const hasStartDatePassed = dateRange.start && DateTime.fromISO(dateRange.start).startOf('week') < now.startOf('week');

		// If there is an order cutoff date and the current date is before the cutoff, set the current date to that
		if (restaurant.orderCutoffDate && restaurant.orderCutoffDate > now) {
			beforeOrderCutoff = true;
			now = restaurant.orderCutoffDate;
		}

		// If there is no date range or if the week has passed, set it to the start of current week and add query param
		if ((!dateRange.start || !dateRange.end) || hasStartDatePassed || beforeOrderCutoff) {
			await dispatch('setDateRange', {
				start: now.startOf('week').toISODate(),
				end: now.endOf('week').toISODate()
			});
			router.replace({ query: { ...route.query, selectedWeek: state.rotationalMenuDateRange.start } }).catch(() => {});
		}
		else {
			await dispatch('fetchRotationalMenus');
		}
	},

	/**
	 * Fetch rotational menus from the db for the selected date range
	 * Rotational menus are created in the menu manager by adding items to a menu with a specific date, which is separate from the menus in the data.json file
	 * This data isn't stored in the data.json file because it would be too much data to have to fetch in one request (approx. 200 daily menus for a school year)
	 * It also wouldn't be feasible to rebuild the data.json file and refetch it every the user changes the date range, so we fetch the rotational menus separately
	 *
	 * @return {Promise<void>}
	 */
	async fetchRotationalMenus({ commit }): Promise<void> {
		try {
			commit('SET_ROTATIONAL_MENUS_FETCHING', true);
			if (!state.rotationalMenuDateRange.start || !state.rotationalMenuDateRange.end) {
				throw i18n.t('menu.error_no_date_range');
			}
			const { data } = await authState.axiosInstance.get(`${process.env.VUE_APP_API_PREFIX}/rotational-menus?start_date=${state.rotationalMenuDateRange.start}&end_date=${state.rotationalMenuDateRange.end}&ordering_menus=true`);
			commit('SET_ROTATIONAL_MENUS', data);
		}
		catch (error) {
			throw error;
		}
		finally {
			setTimeout(() => {
				commit('SET_ROTATIONAL_MENUS_FETCHING', false);
			}, 300);
		}
	},

	async fetchAnnouncements({ commit }): Promise<void> {
		try {
			const { data } = await authState.axiosInstance.get(`${process.env.VUE_APP_API_PREFIX}/restaurants/${state.restaurant.id}/announcements`);
			commit('SET_ANNOUNCEMENTS', data);
		}
		catch (error) {
			throw error;
		}
	},

	async setDateRange({ commit, dispatch }, dateRange: DateRange): Promise<void> {
		commit('SET_ROTATIONAL_MENU_DATE_RANGE', dateRange);
		await dispatch('fetchRotationalMenus');
	},

	/**
	 * Fetch rotational menus for repeating orders (max 2 months from today)
	 *
	 * @return {Promise<void>}
	 */
	async fetchRepeatingOrdersMenus({ commit, state }): Promise<void> {
		const now = DateTime.now().setZone(state.restaurant.menu_configuration?.zone || 'America/Toronto');
		const startDate = now.startOf('day').toISODate();
		const endDate = now.plus({ months: 2 }).endOf('day').toISODate();

		const { data } = await authState.axiosInstance.get(`${process.env.VUE_APP_API_PREFIX}/rotational-menus?start_date=${startDate}&end_date=${endDate}&ordering_menus=true&repeating_orders=true`);
		commit('SET_REPEATING_ORDERS_MENUS', data);
	},

	setRepeatingOrdersUnavailableDates({ commit }, dates: string[]): void {
		commit('SET_REPEATING_ORDERS_UNAVAILABLE_DATES', dates);
	},

	setRepeatingOrdersSelectedMealType({ commit }, mealType: string): void {
		commit('SET_REPEATING_ORDERS_SELECTED_MEAL_TYPE', mealType);
	},

	resetRestaurantState({ commit }): void {
		commit('RESET_RESTAURANT_STATE');
	}
};